import * as React from "react";
import { Link } from "react-router-dom";
import {
  Space,
  Input,
  Table,
  Skeleton,
  Spin,
  Form,
  message,
  Modal,
  Upload,
  Button,
  Progress,
  Select,
} from "antd";

import {
  UploadOutlined,
  CloseOutlined,
} from "@ant-design/icons";

import { PlusButton } from "components/PlusButton";
import {
  ModelSearchApiCommunicator,
  formatFullDateFromTimeStamp,
  useAmplifyOrgSubscription,
  UploadCommunicator,
  MLModelApiCommunicator,
} from "api";
import { addOrUpdateObjectInList } from "utils";
import { ListPageLayout } from "layouts";
import { useSearch } from "hooks";
import { TableListCellItems } from "components/Model/TableListCellItems";

const { Search } = Input;
const { Option } = Select;

/**
 * ModelSearchListPage Component
 * 
 * Displays a list of all model search results with their current status and basic information.
 * Provides functionality for viewing, searching, and creating new model searches.
 * 
 * Features:
 * - Sortable table with pagination
 * - Real-time search functionality
 * - Model search creation modal with file upload
 * - File upload progress tracking
 * - Real-time updates via Amplify subscriptions
 * - Status tracking and display
 * - Search result management
 * 
 * @returns {JSX.Element} The model search list page component
 */
export function ModelSearchListPage() {
  const [modelSearchApiCommunicator] = React.useState(new ModelSearchApiCommunicator());
  const [mlModelApiCommunicator] = React.useState(new MLModelApiCommunicator());
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  const [messageApi, contextHolder] = message.useMessage();
  const [addModalOpen, openAddModal] = React.useState(false);
  const [uploadCommunicator] = React.useState(new UploadCommunicator());
  const [uploadingPercent, setUploadingPercent] = React.useState(0);
  const [uploading, setUploading] = React.useState(false);
  const [modelSearchResults, setModelSearchResults] = React.useState([]);
  const [printers, setPrinters] = React.useState([]);
  const [materials, setMaterials] = React.useState([]);

  const fetchPageData = React.useCallback(async () => {
    setLoading(true);
    try {
      // Make all API calls in parallel
      const [modelSearchRes, printersRes, materialsRes] = await Promise.all([
        modelSearchApiCommunicator.list(),
        mlModelApiCommunicator.listPrinters(),
        mlModelApiCommunicator.listMaterials()
      ]);
      
      setModelSearchResults(modelSearchRes.data.sort((a, b) => b.createdAt - a.createdAt));
      setPrinters(printersRes.data);
      setMaterials(materialsRes.data);

    } catch (err) {
      messageApi.error(err.message);
    }
    setLoading(false);
  }, [messageApi, modelSearchApiCommunicator, mlModelApiCommunicator]);

  useAmplifyOrgSubscription((update) => {
    console.log("update", update);
    if (update.type === "partvectorsearch" && !loading) {
      const newModelSearchResults = addOrUpdateObjectInList(
        modelSearchResults,
        update.data,
        "partVectorSearchId"
      );
      setModelSearchResults(newModelSearchResults.sort((a, b) => b.createdAt - a.createdAt));
    }
  });

  React.useEffect(() => {
    fetchPageData();
  }, [fetchPageData]);

  const {
    query: searchTerm,
    setQuery: setSearchTerm,
    loading: isSearching,
    results: searchResults,
    search,
  } = useSearch(
    true,
    500,
    "mlmodel",
    React.useCallback(
      (msg) => messageApi.error("Error searching: " + msg),
      [messageApi]
    )
  );


  const updateUploadProgress = React.useCallback(
    async (progressEvent) => {
      if (progressEvent.event.lengthComputable) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setUploadingPercent(percent);
      }
    },
    [setUploadingPercent]
  );


  const addSearch = React.useCallback(
    async (values) => {
      openAddModal(false);
      setLoading(true);
      const file = values.upload.file.originFileObj;
      const fileName = values.upload.file.name;
      const printerId = values.printerId;
      const materialId = values.materialId;

      console.log("Adding model search file:", fileName);

      let createRes;
      try {
        createRes = await modelSearchApiCommunicator.create({
          fileName,
          printerId,
          materialId,
        });
      } catch (err) {
        messageApi.error("Error creating model: " + err.message);
        setLoading(false);
        return;
      }
      
      try {
        setUploading(true);
        await uploadCommunicator.uploadFile(
          createRes.data.uploadUrl,
          file,
          updateUploadProgress
        );
      } catch (err) {
        messageApi.error("Error uploading model search file: " + err.message);
      }

      setTimeout(() => {
        setUploading(false);
        setUploadingPercent(0);
        
        const messageKey = 'modelSearchSubmitted';
        const closeMessage = () => {
          messageApi.destroy(messageKey);
        };
        
        messageApi.open({
          key: messageKey,
          content: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Model search submitted successfully! Searches may take a few minutes to return results. Please be patient while we process your request.</span>
              <CloseOutlined 
                onClick={closeMessage} 
                style={{ cursor: 'pointer', marginLeft: 8, color: '#666' }} 
              />
            </div>
          ),
          duration: 10,
        });
        
        fetchPageData();
        setLoading(false);
      }, 1000);
    },
    [modelSearchApiCommunicator, messageApi, uploadCommunicator, updateUploadProgress, fetchPageData]
  );

  const handleSearchValueChange = (e) => {
    const { value: inputValue } = e.target;
    setSearchTerm(inputValue);
  };

  const columns = [
    {
      title: "File Name",
      key: "fileName",
      render: (_, r) => (
        <Link to={`/modelsearch/${r.partVectorSearchId }`}>{r.fileName}</Link>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => <TableListCellItems.StatusBadge status={r.status} />,
    },
    {
      title: "Added",
      key: "createdAt",
      render: (_, r) => <div>{formatFullDateFromTimeStamp(r.createdAt)}</div>,
    }
  ];

  return (
    <ListPageLayout>
      {contextHolder}
      {uploading && <Progress percent={uploadingPercent} />}
      <Space
        direction="vertical"
        size="middle"
        style={{
          display: "flex",
        }}
      >
        {loading ? (
          <span>
            <Spin /> {loadingMessage}
          </span>
        ) : (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Space style={{ display: "flex", alignItems: "center" }}>
              <h3 style={{ paddingLeft: "16px" }}>Model Search</h3>
              <PlusButton onClick={() => openAddModal(true)} />
            </Space>
            <Space>
              <Search
                placeholder="search history"
                value={searchTerm}
                onChange={handleSearchValueChange}
                onSearch={search}
                loading={isSearching}
                style={{ width: 200 }}
              />
            </Space>
          </div>
        )}
        <Skeleton loading={loading}>
          {searchTerm && searchResults ? (
            <Table
              columns={columns}
              dataSource={searchResults}
              rowKey="partVectorSearchId"
              locale={{
                emptyText: "No data matching your search term " + searchTerm,
              }}
            />
          ) : (
            <Table columns={columns} dataSource={modelSearchResults} rowKey="partVectorSearchId" />
          )}
        </Skeleton>
        <AddModal
          open={addModalOpen}
          onCancel={() => openAddModal(false)}
          onSubmit={addSearch} 
          printers={printers}
          materials={materials}
        />
      </Space>
    </ListPageLayout>
  );
}

function AddModal({ open = false, onCancel, onSubmit, printers, materials }) {
  const [form] = Form.useForm();
  const [fileList, setFileList] = React.useState([]);
  const onFileChange = React.useCallback(
    (fs) => {
      // manually set status for antd's rendering
      for (const f of fs.fileList) {
        f.status = "done";
      }
      setFileList([...fs.fileList]);
      // Set modelName to the name of the first file
      if (fs.fileList[0]) {
        form.setFieldsValue({ modelName: fs.fileList[0].name });
      }
    },
    [form, setFileList]
  );
  return (
    <Modal
      title="Add Search File"
      open={open}
      onCancel={onCancel}
      okText="Add"
      afterOpenChange={(open) => {
        if (!open) {
          form.resetFields();
          setFileList([]);
        }
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSubmit(values);
            form.resetFields();
          })
          .catch((info) => {
            // console.error("validation failed:", info);
          });
      }}
    >
      <Form form={form} layout="vertical">

      <Form.Item
          label="Printer"
          name="printerId"
          rules={[{ required: true, message: "Printer is required" }]}
        >
          <Select placeholder="Select a printer">
            <Option value="">Select a printer</Option>
            {printers.map((printer) => (
              <Option key={printer.printerId} value={printer.printerId}>
                {printer.printerName}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Material"
          name="materialId"
          rules={[{ required: true, message: "Material is required" }]}
        >
          <Select placeholder="Select a material">
            <Option value="">Select a material</Option>
            {materials.map((material) => (
              <Option key={material.materialId} value={material.materialId}>
                {material.materialName}
              </Option>
            ))}
          </Select>
        </Form.Item>

          <Form.Item
            label="Search File Upload"
            name="upload"
            rules={[{ required: true, message: "Print file is required" }]}
          >
            <Upload
              accept=".3mf"
              maxCount={1}
              customRequest={() => {}}
              onChange={onFileChange}
              fileList={fileList}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
      </Form>
    </Modal>
  );
}
